import Button from '@/components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAssessmentStore } from '../../store/useAssessmentStore';
import { useCallback, useMemo, useState } from 'react';
import { useStartNewAssessment } from '../../hooks/useStartNewAssessment';
import { CohortAssessmentStatus } from '@edwin-group/cj-graphql';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMissionRouteParams } from '@/pages/mission/shared/hooks/useMissionRouteParams';
import { AssessmentStatus, useChapterStore } from '@/pages/mission/shared/store/useChapterStore';
import { YouHaventFilledPreAssessment } from '../you-havent-filled-pre-assessment';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export const ChooseCohort = () => {
  const cohorts = useAssessmentStore((state) => state.cohorts);
  const assessmentPage = useAssessmentStore((state) => state.assessmentPage);
  const navigate = useNavigate();
  const [selectedCohort, setSelectedCohort] = useState<string>('');
  const { missionSlug, chapterTitleEncoded } = useMissionRouteParams();
  const status = useChapterStore((state) => state.status);
  const { shouldEnableStartNewAssessment, cohortHasPreAssessment } = useStartNewAssessment(selectedCohort);
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');

  const getCohortInfo = useCallback((assessmentStatus: CohortAssessmentStatus) => {
    if (!assessmentStatus) return '';

    if (assessmentStatus === CohortAssessmentStatus.PreAssessmentCreated) {
      return 'Pre-Assessment Submitted';
    }

    if (assessmentStatus === CohortAssessmentStatus.PostAssessmentCreated) {
      return 'Post-Assessment Submitted';
    }

    return '';
  }, []);

  const cohortOptions = useMemo(
    () =>
      cohorts?.map((cohort) => ({
        label: cohort.name,
        value: cohort.documentId,
      })) || [],
    [cohorts],
  );

  const onCreateCohort = () => {
    navigate(`/mission/${missionSlug}/chapter/${chapterTitleEncoded}/cohort/create`);
  };

  const onSelectCohort = async () => {
    if (!selectedCohort) {
      alert(assessmentPage?.cohortSection?.pleaseSelectACohortMessage);
      return;
    }

    navigate(`/mission/${missionSlug}/chapter/${chapterTitleEncoded}/assessment/${selectedCohort}`);
  };

  const cohortInfo = useMemo(() => {
    const cohort = cohorts?.find((c) => c.documentId === selectedCohort);
    if (!cohort) return '';
    return getCohortInfo(cohort.assessmentStatus || CohortAssessmentStatus.NoAssessmentCreated);
  }, [cohorts, selectedCohort, getCohortInfo]);

  if (cohorts?.length === 0) return <></>;

  return (
    <div className="mt-4 flex flex-col gap-4">
      {from && (
        <Link
          to={`/mission/${missionSlug}/chapter/${encodeURIComponent(from)}/assessment`}
          className="flex items-center gap-2 text-primary-600 visited:text-primary-600 hover:!text-primary-700">
          <ArrowLeftIcon className="h-4 w-4" /> <span className="text-sh-xss">Back to post assessment</span>
        </Link>
      )}
      <h2 className="font-cadiz text-sh-28-32 font-bold">{assessmentPage?.cohortSection?.chooseYourCohort}</h2>
      <div className="w-1/2">
        <label className="mb-2 block font-cadiz text-sh-sm font-bold text-gray-700">{`Cohorts`}</label>
        <Select onValueChange={setSelectedCohort} value={selectedCohort}>
          <SelectTrigger>
            <SelectValue placeholder="Select a cohort" />
          </SelectTrigger>
          <SelectContent>
            {cohortOptions.map((cohort) => (
              <SelectItem key={cohort.value} value={cohort.value}>
                {cohort.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedCohort && cohortInfo && <p className="mb-2 mt-sh-5 text-sh-xxs text-green-800">{cohortInfo}</p>}
      </div>

      {selectedCohort && status === AssessmentStatus.POST_ASSESSMENT && !cohortHasPreAssessment && (
        <YouHaventFilledPreAssessment selectedCohortId={selectedCohort} />
      )}

      <div className="flex gap-4">
        <Button
          theme="solid-primary-600"
          onClick={onSelectCohort}
          disabled={
            !shouldEnableStartNewAssessment ||
            !selectedCohort ||
            (!cohortHasPreAssessment && status === AssessmentStatus.POST_ASSESSMENT)
          }>
          {assessmentPage?.cohortSection?.nextButton}
        </Button>

        <Button theme="outline" onClick={onCreateCohort}>
          {assessmentPage?.cohortSection?.createANewCohortPlaceholder}
        </Button>
      </div>
    </div>
  );
};
